import type { components } from '@luxuryescapes/contract-svc-promo/generated';

import { toPromoBrand } from '~/consts/promoBrand';

import { useAppSelector } from './store';

type definitions = components['schemas'];

type CurrentPromoBrand =
  | {
      hasValidPromoBrand: true;
      brand: string;
      promoBrand: definitions['Promo Brands'];
    }
  | {
      hasValidPromoBrand: false;
      brand: string;
      promoBrand: definitions['Promo Brands'];
      failMessage: string;
    };

type UseCurrentPromoBrandProps = {
  failMode?: 'defaultToLE' | 'error' | 'warning';
};

export default function useCurrentPromoBrand({ failMode }: UseCurrentPromoBrandProps = {}): CurrentPromoBrand {
  const brand = useAppSelector((state) => state.tenant.brand);
  let promoBrand = toPromoBrand(brand);
  let failMessage = `Could not get promo brand for ${brand}. Contact Growth to have it configured.`;
  if (!promoBrand) {
    switch (failMode) {
      default:
      case 'defaultToLE': {
        console.warn(
          `useCurrentPromoBrand: tenant brand "${brand}" is not a valid promo brand, defaulting to luxuryescapes`,
        );
        promoBrand = 'luxuryescapes';
        failMessage = `defaulting to "luxuryescapes" - ${failMessage}`;
        break;
      }
      case 'error': {
        throw new Error(`useCurrentPromoBrand: tenant brand "${brand}" is not a valid promo brand,`);
      }
      case 'warning': {
        break;
      }
    }
  }

  if (promoBrand) {
    return {
      hasValidPromoBrand: true,
      brand,
      promoBrand,
    };
  }

  return {
    hasValidPromoBrand: false,
    brand,
    promoBrand: 'luxuryescapes',
    failMessage,
  };
}

export function hasInvalidPromoBrand(
  tenant: CurrentPromoBrand,
): tenant is Extract<CurrentPromoBrand, { hasValidPromoBrand: false }> {
  return !tenant.hasValidPromoBrand;
}
